import { formatMinutes, parseDurationToMinutes } from '@jira/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Clock, History, Link2, MessageSquare, Paperclip, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { EmptyState } from '@/components/common/empty-state';
import { Markdown } from '@/components/common/markdown';
import { MarkdownEditor } from '@/components/common/markdown-editor';
import type { ProjectLayoutContext } from '@/components/layout/project-layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import {
  createComment,
  createIssueLink,
  createTimeLog,
  deleteCommentApi,
  deleteIssueLinkApi,
  deleteTimeLogApi,
  listComments,
  listTimeLogs,
  toggleReactionApi,
  updateCommentBody,
  type CommentRow,
  type TimeLogRow,
} from '@/lib/collab-api';
import { listMembers } from '@/lib/org-api';
import {
  deleteIssue,
  getIssue,
  listIssues,
  listStatuses,
  updateIssue,
  type IssueDetail,
  type IssuePriority,
} from '@/lib/project-api';
import { useAuthStore } from '@/stores/auth-store';

const PRIORITIES: IssuePriority[] = ['LOWEST', 'LOW', 'MEDIUM', 'HIGH', 'HIGHEST'];
const LINK_TYPES = [
  { value: 'BLOCKS', label: 'Blocks' },
  { value: 'IS_BLOCKED_BY', label: 'Is blocked by' },
  { value: 'RELATES_TO', label: 'Relates to' },
  { value: 'DUPLICATES', label: 'Duplicates' },
  { value: 'DUPLICATED_BY', label: 'Duplicated by' },
] as const;
const REACTION_OPTIONS = [':+1:', ':-1:', ':tada:', ':eyes:'];

type ActivityFilter = 'ALL' | 'COMMENTS' | 'HISTORY';

export function IssueDetailPage() {
  const { orgId, orgSlug, projectKey } = useOutletContext<ProjectLayoutContext>();
  const { issueKey } = useParams<{ issueKey: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { success, error } = useToast();
  const user = useAuthStore((s) => s.user);
  const membership = user?.memberships.find((m) => m.organizationId === orgId);
  const canEdit = Boolean(user?.isSuperAdmin || membership?.permissions.includes('issue.edit'));
  const canComment = Boolean(
    user?.isSuperAdmin || membership?.permissions.includes('issue.comment'),
  );
  const canLogTime = Boolean(
    user?.isSuperAdmin || membership?.permissions.includes('issue.log_time'),
  );
  const canDelete = Boolean(user?.isSuperAdmin || membership?.permissions.includes('issue.delete'));

  const issueQuery = useQuery({
    queryKey: ['org', orgId, 'issue', issueKey],
    queryFn: () => getIssue(orgId, issueKey ?? ''),
    enabled: Boolean(issueKey),
  });

  const statusesQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'statuses'],
    queryFn: () =>
      issueQuery.data ? listStatuses(orgId, issueQuery.data.project.id) : Promise.resolve([]),
    enabled: Boolean(issueQuery.data),
  });

  const membersQuery = useQuery({
    queryKey: ['org', orgId, 'members', 'compact'],
    queryFn: () => listMembers(orgId, { limit: 100 }),
  });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);

  useEffect(() => {
    if (issueQuery.data) {
      setTitle(issueQuery.data.title);
      setDescription(issueQuery.data.descriptionMarkdown ?? '');
    }
  }, [issueQuery.data]);

  const patchMutation = useMutation({
    mutationFn: (body: Partial<Parameters<typeof updateIssue>[2]>) =>
      updateIssue(orgId, issueKey ?? '', body as never),
    onSuccess: (updated) => {
      qc.setQueryData(['org', orgId, 'issue', updated.key], updated);
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'issues'] });
    },
    onError: (e) => error('Update failed', extractApiErrorMessage(e)),
  });

  const removeMutation = useMutation({
    mutationFn: () => deleteIssue(orgId, issueKey ?? ''),
    onSuccess: () => {
      success('Issue deleted');
      navigate(`/o/${orgSlug}/projects/${projectKey}/issues`);
    },
    onError: (e) => error('Delete failed', extractApiErrorMessage(e)),
  });

  if (issueQuery.isLoading) {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-4 p-6">
        <Skeleton className="h-8 w-1/3" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-96 lg:col-span-2" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (issueQuery.isError || !issueQuery.data) {
    return (
      <div className="mx-auto w-full max-w-6xl p-6">
        <EmptyState
          title="Could not load issue"
          description={extractApiErrorMessage(issueQuery.error ?? new Error('Not found'))}
          action={
            <Button asChild>
              <Link to={`/o/${orgSlug}/projects/${projectKey}/issues`}>Back to issues</Link>
            </Button>
          }
        />
      </div>
    );
  }

  const issue: IssueDetail = issueQuery.data;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 p-6">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Link
          to={`/o/${orgSlug}/projects/${projectKey}/issues`}
          className="inline-flex items-center gap-1 hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden /> All issues
        </Link>
        <span>·</span>
        {issue.parent ? (
          <>
            <Link
              to={`/o/${orgSlug}/projects/${issue.project.key}/issues/${issue.parent.key}`}
              className="font-mono hover:text-foreground"
            >
              {issue.parent.key}
            </Link>
            <span>·</span>
          </>
        ) : null}
        <span className="font-mono">{issue.key}</span>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div className="flex-1">
                {editingTitle && canEdit ? (
                  <form
                    className="flex gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (title.trim() && title.trim() !== issue.title) {
                        patchMutation.mutate({ title: title.trim() });
                      }
                      setEditingTitle(false);
                    }}
                  >
                    <Input
                      autoFocus
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      onBlur={() => setEditingTitle(false)}
                    />
                    <Button type="submit" size="sm">
                      Save
                    </Button>
                  </form>
                ) : (
                  <CardTitle
                    className={`text-xl ${canEdit ? 'cursor-text hover:text-primary' : ''}`}
                    onClick={() => canEdit && setEditingTitle(true)}
                  >
                    {issue.title}
                  </CardTitle>
                )}
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline">{issue.type}</Badge>
                  <Badge variant="secondary">{issue.statusName}</Badge>
                </div>
              </div>
              {canDelete ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Delete issue"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              ) : null}
            </CardHeader>
            <CardContent className="space-y-4">
              <section>
                <Label className="text-xs uppercase text-muted-foreground">Description</Label>
                {editingDescription && canEdit ? (
                  <div className="mt-2 space-y-2">
                    <MarkdownEditor
                      value={description}
                      onChange={setDescription}
                      orgId={orgId}
                      issueId={issue.id}
                      projectId={issue.project.id}
                      placeholder="Use markdown. Drop or paste files to attach."
                      minHeight={200}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          patchMutation.mutate({
                            descriptionMarkdown: description.trim() ? description : null,
                          });
                          setEditingDescription(false);
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setDescription(issue.descriptionMarkdown ?? '');
                          setEditingDescription(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`mt-2 rounded-md border bg-muted/10 p-3 ${canEdit ? 'cursor-text' : ''}`}
                    onClick={() => canEdit && setEditingDescription(true)}
                  >
                    <Markdown
                      source={issue.descriptionMarkdown}
                      emptyPlaceholder={canEdit ? 'Click to add a description…' : 'No description.'}
                    />
                  </div>
                )}
              </section>

              {issue.subtasks.length ? (
                <section>
                  <Label className="text-xs uppercase text-muted-foreground">Subtasks</Label>
                  <ul className="mt-2 divide-y rounded-md border">
                    {issue.subtasks.map((sub) => (
                      <li
                        key={sub.id}
                        className="flex items-center justify-between gap-3 p-2 text-sm"
                      >
                        <Link
                          to={`/o/${orgSlug}/projects/${issue.project.key}/issues/${sub.key}`}
                          className="flex items-center gap-2"
                        >
                          <span className="font-mono text-xs text-primary">{sub.key}</span>
                          <span className="truncate">{sub.title}</span>
                        </Link>
                        <Badge variant="outline">{sub.statusName}</Badge>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <LinkedIssuesSection
                issue={issue}
                orgId={orgId}
                orgSlug={orgSlug}
                canEdit={canEdit}
                onLinksChanged={() =>
                  qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issueKey] })
                }
              />
            </CardContent>
          </Card>

          <AttachmentsCard issue={issue} />

          <CommentsCard
            issue={issue}
            orgId={orgId}
            canComment={canComment}
            userId={user?.id ?? ''}
          />

          <ActivityCard issue={issue} />
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Status</Label>
                <Select
                  value={issue.statusId}
                  disabled={!canEdit || patchMutation.isPending}
                  onChange={(e) => patchMutation.mutate({ statusId: e.target.value })}
                  className="mt-1"
                >
                  {statusesQuery.data?.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Priority</Label>
                <Select
                  value={issue.priority}
                  disabled={!canEdit || patchMutation.isPending}
                  onChange={(e) =>
                    patchMutation.mutate({ priority: e.target.value as IssuePriority })
                  }
                  className="mt-1"
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Assignee</Label>
                <Select
                  value={issue.assigneeId ?? ''}
                  disabled={!canEdit || patchMutation.isPending}
                  onChange={(e) =>
                    patchMutation.mutate({
                      assigneeId: e.target.value ? e.target.value : null,
                    })
                  }
                  className="mt-1"
                >
                  <option value="">Unassigned</option>
                  {membersQuery.data?.items.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.name} ({m.email})
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Story points</Label>
                <Input
                  type="number"
                  min={0}
                  max={999}
                  disabled={!canEdit || patchMutation.isPending}
                  defaultValue={issue.storyPoints ?? ''}
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    const next = raw === '' ? null : Number(raw);
                    if (next === issue.storyPoints) return;
                    if (next !== null && Number.isNaN(next)) return;
                    patchMutation.mutate({ storyPoints: next });
                  }}
                />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Reporter</Label>
                <p className="mt-1 text-sm">
                  {issue.reporter?.name ?? '—'}
                  {issue.reporter ? (
                    <span className="text-xs text-muted-foreground"> · {issue.reporter.email}</span>
                  ) : null}
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Created / Updated</Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(issue.createdAt).toLocaleString()}
                  <br />
                  Updated {new Date(issue.updatedAt).toLocaleString()}
                </p>
              </div>
            </CardContent>
          </Card>

          <TimeTrackingCard issue={issue} orgId={orgId} canLog={canLogTime} />
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${issue.key}?`}
        description="This removes the issue, its comments, time logs, attachments, and activity log."
        confirmLabel="Delete issue"
        destructive
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          await removeMutation.mutateAsync();
        }}
      />
    </div>
  );
}

// ---------- Linked issues ----------

function LinkedIssuesSection({
  issue,
  orgId,
  orgSlug,
  canEdit,
  onLinksChanged,
}: {
  issue: IssueDetail;
  orgId: string;
  orgSlug: string;
  canEdit: boolean;
  onLinksChanged: () => void;
}) {
  const { error, success } = useToast();
  const [adding, setAdding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [linkType, setLinkType] = useState<(typeof LINK_TYPES)[number]['value']>('RELATES_TO');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const results = useQuery({
    queryKey: ['issue-search', orgId, issue.project.id, debounced],
    queryFn: () =>
      listIssues(orgId, issue.project.id, {
        searchText: debounced,
        limit: 10,
      }),
    enabled: adding && debounced.length >= 2,
  });

  const linkMutation = useMutation({
    mutationFn: (targetIssueId: string) =>
      createIssueLink(orgId, issue.id, { targetIssueId, linkType }).then(() => undefined),
    onSuccess: () => {
      success('Linked');
      setAdding(false);
      setSearchQuery('');
      onLinksChanged();
    },
    onError: (e) => error('Could not link', extractApiErrorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (linkId: string) => deleteIssueLinkApi(orgId, linkId),
    onSuccess: () => {
      success('Link removed');
      onLinksChanged();
    },
    onError: (e) => error('Could not unlink', extractApiErrorMessage(e)),
  });

  return (
    <section>
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2 text-xs uppercase text-muted-foreground">
          <Link2 className="h-3 w-3" aria-hidden /> Linked issues
        </Label>
        {canEdit ? (
          <Button size="sm" variant="outline" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : 'Add link'}
          </Button>
        ) : null}
      </div>

      {issue.linkedIssues.length === 0 && !adding ? (
        <p className="mt-2 text-xs italic text-muted-foreground">No linked issues yet.</p>
      ) : (
        <ul className="mt-2 divide-y rounded-md border">
          {issue.linkedIssues.map((link) => (
            <li key={link.id} className="flex items-center justify-between gap-3 p-2 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{link.linkType.replace(/_/g, ' ')}</Badge>
                <Link
                  to={`/o/${orgSlug}/projects/${issue.project.key}/issues/${link.issue.key}`}
                  className="font-mono text-xs text-primary"
                >
                  {link.issue.key}
                </Link>
                <span className="truncate">{link.issue.title}</span>
              </div>
              {canEdit ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => deleteMutation.mutate(link.id)}
                  disabled={deleteMutation.isPending}
                  aria-label="Remove link"
                >
                  <Trash2 className="h-3 w-3" aria-hidden />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <div className="mt-3 space-y-2 rounded-md border bg-muted/10 p-3">
          <div className="flex gap-2">
            <Select
              value={linkType}
              onChange={(e) => setLinkType(e.target.value as typeof linkType)}
            >
              {LINK_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
            <Input
              placeholder="Search by key or title…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="max-h-48 overflow-auto rounded-md border bg-background">
            {debounced.length < 2 ? (
              <p className="p-3 text-xs text-muted-foreground">Type at least 2 characters.</p>
            ) : results.isLoading ? (
              <div className="space-y-2 p-2">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : (results.data?.items.length ?? 0) === 0 ? (
              <p className="p-3 text-xs text-muted-foreground">No matches.</p>
            ) : (
              <ul className="divide-y">
                {results
                  .data!.items.filter((item) => item.id !== issue.id)
                  .map((item) => (
                    <li
                      key={item.id}
                      className="flex items-center justify-between gap-2 p-2 text-sm"
                    >
                      <span className="font-mono text-xs text-primary">{item.key}</span>
                      <span className="flex-1 truncate">{item.title}</span>
                      <Button
                        size="sm"
                        onClick={() => linkMutation.mutate(item.id)}
                        disabled={linkMutation.isPending}
                      >
                        Link
                      </Button>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

// ---------- Attachments ----------

function AttachmentsCard({ issue }: { issue: IssueDetail }) {
  if (!issue.attachments.length) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Paperclip className="h-4 w-4" aria-hidden /> Attachments
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="grid gap-2 sm:grid-cols-2">
          {issue.attachments.map((att) => {
            const isImage = att.mimeType.startsWith('image/');
            return (
              <li
                key={att.id}
                className="flex items-center gap-3 rounded-md border bg-muted/10 p-2 text-sm"
              >
                {isImage ? (
                  <img
                    src={`/api/uploads/${att.id}?variant=thumbnail`}
                    alt={att.originalFilename}
                    className="h-14 w-14 shrink-0 rounded-md border object-cover"
                  />
                ) : (
                  <Paperclip className="h-8 w-8 shrink-0 text-muted-foreground" aria-hidden />
                )}
                <div className="min-w-0 flex-1">
                  <a
                    href={`/api/uploads/${att.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate font-medium hover:underline"
                  >
                    {att.originalFilename}
                  </a>
                  <div className="text-xs text-muted-foreground">
                    {Math.round(att.sizeBytes / 1024)} KB · {att.mimeType}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

// ---------- Comments ----------

function CommentsCard({
  issue,
  orgId,
  canComment,
  userId,
}: {
  issue: IssueDetail;
  orgId: string;
  canComment: boolean;
  userId: string;
}) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [body, setBody] = useState('');
  const [filter, setFilter] = useState<ActivityFilter>('ALL');

  const query = useQuery({
    queryKey: ['org', orgId, 'issue', issue.id, 'comments'],
    queryFn: () => listComments(orgId, issue.id, { limit: 50 }),
  });

  const add = useMutation({
    mutationFn: () => createComment(orgId, issue.id, body),
    onSuccess: () => {
      success('Comment posted');
      setBody('');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.id, 'comments'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.key] });
    },
    onError: (e) => error('Comment failed', extractApiErrorMessage(e)),
  });

  const visibleItems = useMemo(() => {
    const items = query.data?.items ?? [];
    if (filter === 'HISTORY') return [];
    return items;
  }, [query.data?.items, filter]);

  return (
    <Card id="comments">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-4 w-4" aria-hidden /> Comments
          {query.data ? (
            <Badge variant="secondary" className="ml-1">
              {query.data.total}
            </Badge>
          ) : null}
        </CardTitle>
        <div className="flex gap-1 text-xs">
          {(['ALL', 'COMMENTS', 'HISTORY'] as ActivityFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={[
                'rounded-full border px-2 py-0.5',
                filter === f
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-transparent text-muted-foreground',
              ].join(' ')}
            >
              {f === 'ALL' ? 'All' : f === 'COMMENTS' ? 'Comments' : 'History'}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {canComment ? (
          <form
            className="space-y-2"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (body.trim() && !add.isPending) add.mutate();
            }}
          >
            <MarkdownEditor
              value={body}
              onChange={setBody}
              orgId={orgId}
              issueId={issue.id}
              projectId={issue.project.id}
              placeholder="Add a comment — use @email to mention someone"
              minHeight={140}
            />
            <div className="flex items-center justify-end gap-2">
              <Button type="submit" disabled={!body.trim() || add.isPending}>
                {add.isPending ? 'Posting…' : 'Post comment'}
              </Button>
            </div>
          </form>
        ) : (
          <p className="text-xs text-muted-foreground">
            You don&apos;t have permission to comment on this issue.
          </p>
        )}

        {query.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : query.isError ? (
          <EmptyState
            title="Could not load comments"
            description={extractApiErrorMessage(query.error)}
            action={<Button onClick={() => query.refetch()}>Retry</Button>}
          />
        ) : visibleItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">No comments yet.</p>
        ) : (
          <ul className="space-y-3">
            {visibleItems.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                orgId={orgId}
                issueId={issue.id}
                userId={userId}
                onChanged={() => {
                  qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.id, 'comments'] });
                  qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.key] });
                }}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function CommentItem({
  comment,
  orgId,
  issueId,
  userId,
  onChanged,
}: {
  comment: CommentRow;
  orgId: string;
  issueId: string;
  userId: string;
  onChanged: () => void;
}) {
  const { success, error } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.bodyMarkdown);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const edit = useMutation({
    mutationFn: () => updateCommentBody(orgId, comment.id, draft),
    onSuccess: () => {
      success('Comment updated');
      setEditing(false);
      onChanged();
    },
    onError: (e) => error('Edit failed', extractApiErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: () => deleteCommentApi(orgId, comment.id),
    onSuccess: () => {
      success('Comment deleted');
      setConfirmDelete(false);
      onChanged();
    },
    onError: (e) => error('Delete failed', extractApiErrorMessage(e)),
  });

  const react = useMutation({
    mutationFn: (emoji: string) => toggleReactionApi(orgId, comment.id, emoji),
    onSuccess: () => onChanged(),
  });

  return (
    <li className="rounded-md border bg-background p-3" id={`comment-${comment.id}`}>
      <div className="flex items-center justify-between gap-2 text-xs">
        <div>
          <span className="font-medium text-foreground">{comment.authorName}</span>
          <span className="text-muted-foreground">
            {' '}
            · {new Date(comment.createdAt).toLocaleString()}
          </span>
          {comment.revisionsCount > 0 ? (
            <Badge variant="outline" className="ml-2">
              Edited
            </Badge>
          ) : null}
          {comment.deletedAt ? (
            <Badge variant="outline" className="ml-2">
              Deleted
            </Badge>
          ) : null}
        </div>
        {!comment.deletedAt && !editing ? (
          <div className="flex gap-1">
            {comment.canEdit ? (
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                Edit
              </Button>
            ) : null}
            {comment.canDelete ? (
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="h-3 w-3" aria-hidden />
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="mt-2">
        {editing ? (
          <div className="space-y-2">
            <MarkdownEditor
              value={draft}
              onChange={setDraft}
              orgId={orgId}
              issueId={issueId}
              minHeight={140}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => edit.mutate()}
                disabled={edit.isPending || !draft.trim()}
              >
                {edit.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Markdown source={comment.bodyMarkdown} emptyPlaceholder="[deleted]" />
        )}
      </div>

      {!comment.deletedAt ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {REACTION_OPTIONS.map((emoji) => {
            const existing = comment.reactions.find((r) => r.emoji === emoji);
            const reacted = existing?.userIds.includes(userId);
            return (
              <button
                key={emoji}
                type="button"
                onClick={() => react.mutate(emoji)}
                className={[
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                  reacted
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-transparent bg-muted/40 text-muted-foreground',
                ].join(' ')}
              >
                <span>{emoji}</span>
                {existing ? <span>{existing.count}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmDelete}
        destructive
        title="Delete this comment?"
        description="It will be soft-deleted — your attribution stays but the body is replaced with [deleted]."
        confirmLabel="Delete"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          await remove.mutateAsync();
        }}
      />
    </li>
  );
}

// ---------- Time tracking ----------

function TimeTrackingCard({
  issue,
  orgId,
  canLog,
}: {
  issue: IssueDetail;
  orgId: string;
  canLog: boolean;
}) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const query = useQuery({
    queryKey: ['org', orgId, 'issue', issue.id, 'timelogs'],
    queryFn: () => listTimeLogs(orgId, issue.id),
  });

  const [durationInput, setDurationInput] = useState('');
  const [description, setDescription] = useState('');
  const [loggedAt, setLoggedAt] = useState('');

  const preview = useMemo(() => {
    if (!durationInput.trim()) return null;
    try {
      const minutes = parseDurationToMinutes(durationInput);
      return formatMinutes(minutes);
    } catch {
      return null;
    }
  }, [durationInput]);

  const add = useMutation({
    mutationFn: () =>
      createTimeLog(orgId, issue.id, {
        durationInput,
        ...(description ? { description } : {}),
        ...(loggedAt ? { loggedAt: new Date(loggedAt).toISOString() } : {}),
      }),
    onSuccess: () => {
      success('Time logged');
      setDurationInput('');
      setDescription('');
      setLoggedAt('');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.id, 'timelogs'] });
    },
    onError: (e) => error('Could not log time', extractApiErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteTimeLogApi(orgId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', issue.id, 'timelogs'] });
    },
    onError: (e) => error('Delete failed', extractApiErrorMessage(e)),
  });

  const byUser = useMemo(() => {
    const groups = new Map<string, { userName: string; minutes: number }>();
    for (const row of query.data?.items ?? []) {
      const existing = groups.get(row.userId);
      if (existing) existing.minutes += row.minutes;
      else groups.set(row.userId, { userName: row.userName, minutes: row.minutes });
    }
    return Array.from(groups.values()).sort((a, b) => b.minutes - a.minutes);
  }, [query.data?.items]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4" aria-hidden /> Time tracking
          {query.data ? (
            <Badge variant="secondary" className="ml-auto">
              {formatMinutes(query.data.totalMinutes) || '0m'}
            </Badge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {canLog ? (
          <form
            className="space-y-2 rounded-md border bg-muted/10 p-2 text-xs"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (add.isPending) return;
              if (!durationInput.trim()) return;
              add.mutate();
            }}
          >
            <Label htmlFor="tl-duration" className="text-[11px] uppercase text-muted-foreground">
              Duration
            </Label>
            <Input
              id="tl-duration"
              placeholder="e.g. 1h 30m or 0.5d"
              value={durationInput}
              onChange={(e) => setDurationInput(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              {preview ? `Parses as ${preview}.` : 'Valid: `1h 30m`, `90m`, `0.5d`, `2w`.'}
            </p>
            <Label htmlFor="tl-date" className="text-[11px] uppercase text-muted-foreground">
              Logged at
            </Label>
            <Input
              id="tl-date"
              type="datetime-local"
              value={loggedAt}
              onChange={(e) => setLoggedAt(e.target.value)}
            />
            <Label htmlFor="tl-desc" className="text-[11px] uppercase text-muted-foreground">
              Note
            </Label>
            <Input
              id="tl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional note"
            />
            <Button type="submit" size="sm" disabled={!durationInput.trim() || add.isPending}>
              {add.isPending ? 'Logging…' : 'Log time'}
            </Button>
          </form>
        ) : null}

        {byUser.length > 0 ? (
          <div>
            <Label className="text-[11px] uppercase text-muted-foreground">By user</Label>
            <ul className="mt-1 space-y-1 text-xs">
              {byUser.map((u) => (
                <li key={u.userName} className="flex justify-between">
                  <span>{u.userName}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {formatMinutes(u.minutes)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {query.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (query.data?.items.length ?? 0) === 0 ? (
          <p className="text-xs text-muted-foreground">No time logged yet.</p>
        ) : (
          <ul className="space-y-2">
            {query.data!.items.map((log: TimeLogRow) => (
              <li
                key={log.id}
                className="flex items-start justify-between gap-2 rounded-md border bg-muted/10 p-2 text-xs"
              >
                <div>
                  <div className="font-medium">
                    {log.userName} · {formatMinutes(log.minutes)}
                  </div>
                  <div className="text-muted-foreground">
                    {new Date(log.loggedAt).toLocaleString()}
                  </div>
                  {log.description ? <div className="mt-1">{log.description}</div> : null}
                </div>
                {log.canEdit ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove.mutate(log.id)}
                    aria-label="Delete log"
                  >
                    <Trash2 className="h-3 w-3" aria-hidden />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Activity ----------

function ActivityCard({ issue }: { issue: IssueDetail }) {
  if (issue.activity.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" aria-hidden /> Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">No activity yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4" aria-hidden /> Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {issue.activity.map((entry) => (
            <li key={entry.id} className="rounded-md border bg-muted/10 p-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  {entry.actorName ?? 'System'} · {entry.action}
                </span>
                <span className="text-muted-foreground">
                  {new Date(entry.createdAt).toLocaleString()}
                </span>
              </div>
              <pre className="mt-1 overflow-auto whitespace-pre-wrap break-all text-[11px] text-muted-foreground">
                {JSON.stringify(entry.metadata, null, 2)}
              </pre>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
